import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { useLazyScansByProduct } from "../../scans/hooks/useScansByProduct";
import { Loading } from "../../../components";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface LinkedProductsPriceGraphProps {
  products: Array<{
    id: number;
    productId: number;
    product?: {
      id: number;
      name: string;
    };
  }>;
}

// Couleurs pour différencier les produits
const CHART_COLORS = [
  { border: "rgb(249, 115, 22)", background: "rgba(249, 115, 22, 0.1)" }, // Orange
  { border: "rgb(59, 130, 246)", background: "rgba(59, 130, 246, 0.1)" }, // Blue
  { border: "rgb(16, 185, 129)", background: "rgba(16, 185, 129, 0.1)" }, // Green
  { border: "rgb(236, 72, 153)", background: "rgba(236, 72, 153, 0.1)" }, // Pink
  { border: "rgb(139, 92, 246)", background: "rgba(139, 92, 246, 0.1)" }, // Purple
  { border: "rgb(245, 158, 11)", background: "rgba(245, 158, 11, 0.1)" }, // Amber
  { border: "rgb(20, 184, 166)", background: "rgba(20, 184, 166, 0.1)" }, // Teal
  { border: "rgb(239, 68, 68)", background: "rgba(239, 68, 68, 0.1)" }, // Red
];

export const LinkedProductsPriceGraph: React.FC<
  LinkedProductsPriceGraphProps
> = ({ products }) => {
  const [fetchScans] = useLazyScansByProduct();
  const [allScansData, setAllScansData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAllProductScans = async () => {
      if (!products || products.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const scansPromises = products.map((rp) =>
          fetchScans({
            variables: {
              productId: rp.productId,
              where: {},
              groupBy: "day",
            },
          })
        );

        const results = await Promise.all(scansPromises);

        const processedData = results.map((result, index) => ({
          productName: products[index]?.product?.name || "Produit inconnu",
          scans: result.data?.scansByProduct?.results || [],
        }));

        setAllScansData(processedData);
      } catch (error) {
        console.error("Erreur lors du chargement des scans:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllProductScans();
  }, [products, fetchScans]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loading />
      </div>
    );
  }

  if (!allScansData || allScansData.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-gray-500">Aucune donnée disponible</p>
      </div>
    );
  }

  // Récupérer toutes les dates uniques et les trier
  const allDates = new Set<string>();
  allScansData.forEach((productData) => {
    productData.scans.forEach((scan: any) => {
      allDates.add(scan.createdAt);
    });
  });

  const sortedDates = Array.from(allDates).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  // Formater les labels de dates
  const labels = sortedDates.map((dateStr) => {
    const date = new Date(dateStr);
    const joursSemaine = ["dim", "lun", "mar", "mer", "jeu", "ven", "sam"];
    const jourSemaine = joursSemaine[date.getDay()];
    const jour = date.getDate().toString().padStart(2, "0");
    const mois = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${jourSemaine} ${jour}/${mois}`;
  });

  // Créer un dataset pour chaque produit
  const datasets = allScansData.map((productData, index) => {
    const colorIndex = index % CHART_COLORS.length;
    const color = CHART_COLORS[colorIndex];

    // Créer une map des prix par date
    const priceByDate = new Map<string, number>();
    productData.scans.forEach((scan: any) => {
      priceByDate.set(scan.createdAt, scan.price);
    });

    // Créer les données dans l'ordre des dates triées
    const data = sortedDates.map((date) => priceByDate.get(date) || null);

    return {
      label: productData.productName,
      data: data,
      borderColor: color.border,
      backgroundColor: color.background,
      tension: 0.4,
      borderWidth: 2,
      pointRadius: 4,
      pointBackgroundColor: color.border,
      pointBorderColor: "#fff",
      pointBorderWidth: 2,
      pointHoverRadius: 6,
      pointHoverBackgroundColor: color.border,
      pointHoverBorderColor: "#fff",
      fill: true,
      spanGaps: true, // Pour connecter les points même s'il y a des valeurs nulles
    };
  });

  return (
    <div className="w-full">
      <Line
        options={{
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: {
              display: true,
              position: "top" as const,
              labels: {
                color: "#374151",
                padding: 15,
                font: {
                  size: 11,
                  weight: 500,
                },
                usePointStyle: true,
                pointStyle: "circle",
              },
            },
            tooltip: {
              backgroundColor: "rgba(30, 41, 59, 0.95)",
              padding: 12,
              borderColor: "rgba(51, 65, 85, 1)",
              borderWidth: 1,
              titleColor: "#fff",
              bodyColor: "#fff",
              cornerRadius: 8,
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label) {
                    label += ": ";
                  }
                  if (context.parsed.y !== null) {
                    label += context.parsed.y.toFixed(2) + " €";
                  }
                  return label;
                },
              },
            },
          },
          interaction: {
            mode: "index" as const,
            intersect: false,
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                color: "#6B7280",
                font: {
                  size: 11,
                },
              },
            },
            y: {
              type: "linear" as const,
              display: true,
              position: "left" as const,
              border: {
                display: false,
              },
              grid: {
                color: "rgba(0, 0, 0, 0.05)",
                drawTicks: false,
              },
              ticks: {
                color: "#6B7280",
                font: {
                  size: 11,
                },
                padding: 8,
                callback: function (value) {
                  return Number.isInteger(value) ? value + " €" : null;
                },
              },
            },
          },
        }}
        data={{
          labels,
          datasets,
        }}
      />
    </div>
  );
};
